import React, { useEffect, useRef, useState } from 'react';
import { Parallax } from 'react-materialize';
import photo1 from '../../img/backgrounds/photo-1600356604581-e573833d02e3.avif';
import './styles.scss';

interface Testimonial {
  quote: string;
  author: string;
  role?: string;
  company?: string;
}

/**
 * Testimonials Component
 *
 * Rotating testimonials displayed in a parallax container.
 * Currently uses placeholder content - to be replaced with real testimonials.
 */

const PLACEHOLDER_TESTIMONIALS: Testimonial[] = [
  {
    quote: "Working with JR has transformed my approach to stress and focus. The practical tools I've learned are now part of my daily routine.",
    author: "Sarah M.",
    role: "Executive",
    company: "Tech Company"
  },
  {
    quote: "The organizational workshop brought our team together in a way I didn't expect. We're more present, more connected, and more effective.",
    author: "Michael R.",
    role: "Team Lead",
    company: "Creative Agency"
  },
  {
    quote: "Mindfulness coaching has given me a new perspective on managing my thoughts and emotions. I feel more capable and at ease.",
    author: "Jennifer L.",
    role: "Individual Client"
  }
];

const HOLD_TIME = 15000;
const FADE_TIME = 2000;
const SMALL_DELAY = 50;

export const Testimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const testimonials = PLACEHOLDER_TESTIMONIALS;

  useEffect(() => {
    // Rotate testimonials every 8 seconds
    intervalRef.current = setInterval(() => {
      // Fade out current testimonial
      setIsVisible(false);

      // After fade out completes (1s), wait 1 second, then change content and fade in
      timeoutRef.current = setTimeout(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        // Small delay before fading in to ensure content is updated
        setTimeout(() => {
          setIsVisible(true);
        }, SMALL_DELAY);
      }, FADE_TIME); // 1s fade out + 1s wait
    }, HOLD_TIME);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [testimonials.length]);

  const current = testimonials[currentTestimonial];

  const authorText = current.role && current.company
    ? `${current.author}, ${current.role} at ${current.company}`
    : current.role
    ? `${current.author}, ${current.role}`
    : current.author;

  return (
    <div
      className='parallax-container testimonials-container'
      style={{
        height: '400px',
      }}
    >
      <Parallax
        image={<img alt='' src={photo1} />}
        options={{
          responsiveThreshold: 0,
        }}
      >
        <div className='container center' style={{ position: 'relative', minHeight: '400px' }}>
          <div
            className={`testimonial-content ${isVisible ? 'fade-in' : 'fade-out'}`}
            style={{
              marginTop: '120px',
            }}
          >
            <h2 className='quote white-text'>
              &ldquo;{current.quote}&rdquo;
            </h2>
            <h2 className='quoteattrib white-text'>
              &mdash; {authorText}
            </h2>
          </div>
        </div>
      </Parallax>
    </div>
  );
};
